import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { GUpdateLocationsThirdPartyExclusionMutation } from 'src/graphql/types/generated';

export type MetrobiLocation = NonNullable<
  GUpdateLocationsThirdPartyExclusionMutation['updateLocationsThirdPartyExclusion']
>['locations'][number];

export type RoutingLocation = {
  locationId: string;
  enforceItemScanning?: boolean;
  printDeliverySheetEnabled?: boolean;
  includeFinancialsOnDeliverySheet?: boolean;
  includeInAutomatedRouting?: boolean;
  deliverWithinStoreHours?: boolean;
  scheduledDeliveryTimeSource?: string;
  storeHoursConfirmed?: boolean;
  customerPickup?: boolean;
  routingCohort?: {
    id: string;
    color?: string;
    name: string;
  };
  serviceTime?: number;
};

export type LocationState = {
  routingLocations: RoutingLocation[];
  metrobi?: {
    contextId: string;
    locations: Record<string, MetrobiLocation>;
    pendingIds: string[];
    // Whether a customer-pickup write is in flight. Kept beside the Metrobi
    // fields so it is scoped to the same locations-table context and cleared
    // with it: a write started before a warehouse switch must not release the
    // lock a write in the new context is holding. Separate from pendingIds
    // because that list marks individual rows as Metrobi-pending, which would
    // disable the wrong switches here.
    customerPickupPending?: boolean;
    message?: string;
  };
};

const initialState: LocationState = {
  routingLocations: [],
};

const locationsSlice = createSlice({
  name: 'locations',
  initialState,
  reducers: {
    beginLocationsContext: (state, action: PayloadAction<string>) => {
      // Discard prior-context overrides so they cannot replace fresh server settings.
      state.routingLocations = [];
      state.metrobi = {
        contextId: action.payload,
        locations: {},
        pendingIds: [],
      };
    },
    endLocationsContext: (state, action: PayloadAction<string>) => {
      if (state.metrobi?.contextId === action.payload)
        state.metrobi = undefined;
    },
    setMetrobiPending: (
      state,
      action: PayloadAction<{ contextId: string; ids: string[] }>,
    ) => {
      if (state.metrobi?.contextId !== action.payload.contextId) return;
      state.metrobi.pendingIds = action.payload.ids;
    },
    setCustomerPickupPending: (
      state,
      action: PayloadAction<{ contextId: string; pending: boolean }>,
    ) => {
      if (state.metrobi?.contextId !== action.payload.contextId) return;
      state.metrobi.customerPickupPending = action.payload.pending;
    },
    confirmMetrobiLocations: (
      state,
      action: PayloadAction<{
        contextId: string;
        locations: MetrobiLocation[];
        message: string;
      }>,
    ) => {
      if (state.metrobi?.contextId !== action.payload.contextId) return;
      action.payload.locations.forEach((location) => {
        state.metrobi!.locations[location.id] = location;
      });
      state.metrobi.message = action.payload.message;
    },
    updateRoutingLocations: (
      state: LocationState,
      action: PayloadAction<RoutingLocation[]>,
    ): LocationState => {
      const myLocations = [...state.routingLocations];
      action.payload.forEach((routingLocation) => {
        const foundLocationIndex = myLocations.findIndex(
          (location: RoutingLocation): boolean =>
            location.locationId === routingLocation.locationId,
        );
        if (foundLocationIndex === -1) myLocations.push(routingLocation);
        else
          myLocations[foundLocationIndex] = {
            ...myLocations[foundLocationIndex],
            ...routingLocation,
          };
      });
      return { ...state, routingLocations: myLocations };
    },
  },
});

const { reducer, actions } = locationsSlice;
export const {
  updateRoutingLocations,
  beginLocationsContext,
  endLocationsContext,
  setMetrobiPending,
  setCustomerPickupPending,
  confirmMetrobiLocations,
} = actions;
export default reducer;
